/* ================================================================== */
/*  KuberNodes Extension Host                                         */
/*                                                                    */
/*  The runtime that loads extensions, constructs their sandboxed     */
/*  API instances, and manages activate / deactivate lifecycle.       */
/* ================================================================== */

import * as aiService from '@/services/ai.service';
import * as fileService from '@/services/file.service';
import { useAiAssistantStore } from '@/store/aiAssistantStore';
import { useDeployStore } from '@/store/deployStore';
import { useEditorStore } from '@/store/editorStore';
import { useExtensionStore } from '@/store/extensionStore';
import { useNotificationStore } from '@/store/notificationStore';
import { useUIStore } from '@/store/uiStore';
import { useWorkspaceStore } from '@/store/workspaceStore';
import type {
    AiAPI,
    DeployAPI,
    EditorAPI,
    EventAPI,
    ExtensionAPI,
    ExtensionEventType,
    ExtensionManifest,
    NotificationAPI,
    StorageAPI,
    UiAPI,
    WorkspaceAPI,
} from './types';

/* ================================================================== */
/*  Event Bus — lightweight pub/sub for extension events              */
/* ================================================================== */

type EventHandler = (data: unknown) => void;

class ExtensionEventBus {
  private listeners = new Map<string, Set<EventHandler>>();

  on(event: string, handler: EventHandler): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event)!.add(handler);
    return () => {
      this.listeners.get(event)?.delete(handler);
    };
  }

  emit(event: string, data?: unknown): void {
    const handlers = this.listeners.get(event);
    if (handlers) {
      for (const h of handlers) {
        try {
          h(data);
        } catch (err) {
          console.error(`[ExtensionHost] Event handler error for '${event}':`, err);
        }
      }
    }
  }

  /** Remove all listeners for a specific prefix (extension cleanup) */
  removeByPrefix(prefix: string): void {
    for (const key of this.listeners.keys()) {
      if (key.startsWith(prefix)) {
        this.listeners.delete(key);
      }
    }
  }

  clear(): void {
    this.listeners.clear();
  }
}

/* ================================================================== */
/*  Extension Host singleton                                          */
/* ================================================================== */

class ExtensionHost {
  private eventBus = new ExtensionEventBus();
  /** Track unsubscribe functions per extension for cleanup */
  private extensionCleanup = new Map<string, Array<() => void>>();

  /* ---------------------------------------------------------------- */
  /*  API Factory — creates the sandboxed API for each extension      */
  /* ---------------------------------------------------------------- */

  private createAPI(manifest: ExtensionManifest): ExtensionAPI {
    const extensionId = manifest.id;

    // ---- Editor API ----
    const editor: EditorAPI = {
      getActiveFilePath: () => {
        const { activeTabId, tabs } = useEditorStore.getState();
        const tab = tabs.find((t) => t.id === activeTabId);
        return tab?.path ?? null;
      },
      getActiveFileContent: () => {
        const { activeTabId, openFiles } = useEditorStore.getState();
        if (!activeTabId) return null;
        const entry = openFiles.get(activeTabId);
        return entry ?? null;
      },
      openFile: (path: string, content?: string) => {
        useEditorStore.getState().openFile(path, content ?? '');
      },
      getOpenTabs: () => {
        return useEditorStore.getState().tabs.map((t) => t.path);
      },
      insertText: (text: string) => {
        useEditorStore.getState().requestInsert(text);
      },
    };

    // ---- Notification API ----
    const notifications: NotificationAPI = {
      info: (title, message) =>
        useNotificationStore
          .getState()
          .addNotification({ type: 'info', title, message, source: extensionId }),
      success: (title, message) =>
        useNotificationStore
          .getState()
          .addNotification({ type: 'success', title, message, source: extensionId }),
      warning: (title, message) =>
        useNotificationStore
          .getState()
          .addNotification({ type: 'warning', title, message, source: extensionId }),
      error: (title, message) =>
        useNotificationStore
          .getState()
          .addNotification({ type: 'error', title, message, source: extensionId }),
    };

    // ---- AI API ----
    const ai: AiAPI = {
      sendMessage: (prompt, taskType, context) => {
        useAiAssistantStore.getState().sendMessage(prompt, taskType as 'code_explain', context);
      },
      generate: async (description: string) => {
        const result = await aiService.generate(description);
        return typeof result === 'string' ? result : JSON.stringify(result);
      },
      audit: async (code: string) => {
        const result = await aiService.audit(code);
        return typeof result === 'string' ? result : JSON.stringify(result);
      },
      explain: async (code: string) => {
        const result = await aiService.explain(code);
        return typeof result === 'string' ? result : JSON.stringify(result);
      },
    };

    // ---- Deploy API ----
    const deploy: DeployAPI = {
      compile: async (workspaceId: string) => {
        await useDeployStore.getState().compile(workspaceId);
      },
      deploy: async (workspaceId: string, options?: Record<string, unknown>) => {
        await useDeployStore.getState().deploy(workspaceId, options as never);
      },
      getCompileResult: () => useDeployStore.getState().compileResult,
      getDeployResult: () => useDeployStore.getState().deployResult,
    };

    // ---- UI API ----
    const ui: UiAPI = {
      setActivePanel: (panelId: string) => {
        useUIStore.getState().setActivePanel(panelId as never);
      },
      setShowTerminal: (show: boolean) => {
        useUIStore.getState().setShowTerminal(show);
      },
      getActivePanel: () => useUIStore.getState().activePanel,
      showDialog: async (options) => {
        return useUIStore.getState().showDialog({
          title: options.title,
          message: options.message,
          buttons: options.buttons ?? ['OK'],
          defaultButton: options.defaultButton,
        });
      },
    };

    // ---- Workspace API ----
    const workspace: WorkspaceAPI = {
      getWorkspaceId: () => useWorkspaceStore.getState().activeWorkspace?.id ?? '',
      readFile: async (path: string) => {
        const wsId = useWorkspaceStore.getState().activeWorkspace?.id ?? '';
        return fileService.getFileContent(wsId, path);
      },
      writeFile: async (path: string, content: string) => {
        const wsId = useWorkspaceStore.getState().activeWorkspace?.id ?? '';
        await fileService.saveFile(wsId, path, content);
      },
      listFiles: async (_path?: string) => {
        const wsId = useWorkspaceStore.getState().activeWorkspace?.id ?? '';
        const tree = await fileService.getFileTree(wsId);
        const flatPaths: string[] = [];
        const flatten = (items: Array<{ path: string; children?: unknown[] }>) => {
          for (const item of items) {
            flatPaths.push(item.path);
            if (item.children)
              flatten(item.children as Array<{ path: string; children?: unknown[] }>);
          }
        };
        flatten(tree as Array<{ path: string; children?: unknown[] }>);
        return flatPaths;
      },
    };

    // ---- Event API ----
    const cleanupFns = this.extensionCleanup.get(extensionId) ?? [];
    this.extensionCleanup.set(extensionId, cleanupFns);

    const events: EventAPI = {
      on: (event: ExtensionEventType, handler: (data: unknown) => void) => {
        const unsub = this.eventBus.on(event, handler);
        cleanupFns.push(unsub);
        return unsub;
      },
      emit: (event: string, data?: unknown) => {
        // Prefix custom events with extension id to avoid collisions
        this.eventBus.emit(`${extensionId}:${event}`, data);
      },
    };

    // ---- Storage API (in-memory, scoped to extension) ----
    const extRecord = useExtensionStore.getState().getExtension(extensionId);
    const storageData = extRecord?.storageData ?? {};

    const storage: StorageAPI = {
      get: <T = unknown>(key: string): T | undefined => storageData[key] as T | undefined,
      set: <T = unknown>(key: string, value: T) => {
        storageData[key] = value;
      },
      remove: (key: string) => {
        delete storageData[key];
      },
      clear: () => {
        for (const key of Object.keys(storageData)) {
          delete storageData[key];
        }
      },
    };

    return {
      manifest,
      editor,
      notifications,
      ai,
      deploy,
      ui,
      workspace,
      events,
      storage,
    };
  }

  /* ---------------------------------------------------------------- */
  /*  Lifecycle management                                            */
  /* ---------------------------------------------------------------- */

  async install(manifest: ExtensionManifest): Promise<void> {
    const store = useExtensionStore.getState();

    // Prevent duplicate registration
    if (store.getExtension(manifest.id)) {
      console.warn(`[ExtensionHost] Extension '${manifest.id}' is already registered`);
      return;
    }

    store.registerExtension(manifest);
    console.log(`[ExtensionHost] Installed '${manifest.name}' v${manifest.version}`);
  }

  async activate(extensionId: string): Promise<void> {
    const store = useExtensionStore.getState();
    const ext = store.getExtension(extensionId);

    if (!ext) {
      console.error(`[ExtensionHost] Cannot activate unknown extension '${extensionId}'`);
      return;
    }

    if (ext.state === 'active') return;

    store.setExtensionState(extensionId, 'activating');

    try {
      const api = this.createAPI(ext.manifest);

      // Store the API instance on the extension record so panels can access it
      const extensions = new Map(useExtensionStore.getState().extensions);
      const updatedExt = extensions.get(extensionId);
      if (updatedExt) {
        extensions.set(extensionId, { ...updatedExt, api });
        useExtensionStore.setState({ extensions });
      }

      await ext.manifest.activate(api);
      store.setExtensionState(extensionId, 'active');
      console.log(`[ExtensionHost] Activated '${ext.manifest.name}'`);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      store.setExtensionState(extensionId, 'error', message);
      console.error(`[ExtensionHost] Failed to activate '${extensionId}':`, err);
    }
  }

  async deactivate(extensionId: string): Promise<void> {
    const store = useExtensionStore.getState();
    const ext = store.getExtension(extensionId);

    if (!ext || ext.state !== 'active') return;

    store.setExtensionState(extensionId, 'deactivating');

    try {
      if (ext.manifest.deactivate) {
        await ext.manifest.deactivate();
      }

      // Cleanup event listeners
      const cleanups = this.extensionCleanup.get(extensionId);
      if (cleanups) {
        for (const fn of cleanups) fn();
        this.extensionCleanup.delete(extensionId);
      }

      // Remove custom events
      this.eventBus.removeByPrefix(`${extensionId}:`);

      store.setExtensionState(extensionId, 'inactive');
      console.log(`[ExtensionHost] Deactivated '${ext.manifest.name}'`);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      store.setExtensionState(extensionId, 'error', message);
      console.error(`[ExtensionHost] Failed to deactivate '${extensionId}':`, err);
    }
  }

  async uninstall(extensionId: string): Promise<void> {
    await this.deactivate(extensionId);
    useExtensionStore.getState().unregisterExtension(extensionId);
    console.log(`[ExtensionHost] Uninstalled '${extensionId}'`);
  }

  /** Install and immediately activate an extension */
  async load(manifest: ExtensionManifest): Promise<void> {
    await this.install(manifest);
    await this.activate(manifest.id);
  }

  /* ---------------------------------------------------------------- */
  /*  Platform event emission — called by core IDE code               */
  /* ---------------------------------------------------------------- */

  emitPlatformEvent(event: ExtensionEventType, data?: unknown): void {
    this.eventBus.emit(event, data);
  }

  /* ---------------------------------------------------------------- */
  /*  Query helpers                                                   */
  /* ---------------------------------------------------------------- */

  getActiveExtensions(): ExtensionManifest[] {
    const result: ExtensionManifest[] = [];
    for (const ext of useExtensionStore.getState().extensions.values()) {
      if (ext.state === 'active') result.push(ext.manifest);
    }
    return result;
  }
}

/* ================================================================== */
/*  Singleton export                                                  */
/* ================================================================== */

export const extensionHost = new ExtensionHost();
